import type { Favorite } from './favoritesApi';

/**
 * The lookup a card needs, built once per list.
 *
 * A card knows the Sanity document id it renders and nothing else, so it can answer neither "am I
 * favorited?" nor "which favorite would I delete?" on its own. Both come off one map:
 * `favoritable_ref` → the favorite's own uuid, which is what `DELETE /favorites/{uuid}` takes.
 *
 * A pure function rather than logic inside the hook so it is covered by this package's test suite,
 * which runs without a DOM or React by design.
 */
export type FavoriteIndex = {
  /** True when this ref is among the favorites the list carried. */
  isFavorite: (favoritableRef: string) => boolean;
  /** The uuid to remove for this ref, or undefined when it is not favorited. */
  favoriteUuidOf: (favoritableRef: string) => string | undefined;
};

export const indexFavorites = (favorites: Favorite[] | undefined): FavoriteIndex => {
  // Last write wins, which only matters if the API ever returned two live favorites for one ref.
  // The partial unique index behind it makes that impossible, so this is just a defined outcome
  // rather than a decision.
  const uuidByRef = new Map((favorites ?? []).map((favorite) => [favorite.favoritable_ref, favorite.uuid]));

  return {
    isFavorite: (favoritableRef: string) => uuidByRef.has(favoritableRef),
    favoriteUuidOf: (favoritableRef: string) => uuidByRef.get(favoritableRef),
  };
};
